import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PortfolioSelector from './PortfolioSelector';
import { selectionFromParams, prefilled } from '../lib/reportPrefill';
import { defaultReportDate, maxReportDate } from '../lib/reportDates';
import MaskedDateField from '../../../components/ui/MaskedDateField';

/**
 * Fixed Income Holdings by Maturity Confirmation Popup
 *
 * Popup for generating Fixed Income Holdings by Maturity reports.
 * Uses the shared <PortfolioSelector> (groups vs accounts, @@ aggregate,
 * fallback) so portfolio routing is by item type, not the "@" heuristic.
 */
const FixedIncomeHoldingsByMaturityPopup = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  reportInfo,
  initialParams = null,
  darkMode = false
}) => {
  const [selection, setSelection] = useState({ selectedItem: null, params: {} });
  const [date, setDate] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Printed report-header inputs (Banyan PDF template).
  const [firmName, setFirmName] = useState('Banyan Tree Asset Management, LLC');
  const [accountName, setAccountName] = useState('');
  const [accountCode, setAccountCode] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Set default date to yesterday (Pershing data arrives overnight for the prior day)
      setDate(prefilled(initialParams, 'date', defaultReportDate()));
      setSelection({ selectedItem: null, params: {} });
      setErrors({});
      setCurrency(prefilled(initialParams, 'currency', 'USD'));
      setFirmName(prefilled(initialParams, 'firm_name', 'Banyan Tree Asset Management, LLC'));
      setAccountName(prefilled(initialParams, 'account_name', ''));
      setAccountCode(prefilled(initialParams, 'account_code', ''));
    }
  }, [isOpen, initialParams]);

  // The single thin-params builder used by handleConfirm. Reports always
  // generate lumped; each multi-lot row expands in the viewer (chevron /
  // Expand All), so there is no tax-lots generation option any more.
  const buildParams = (sel) => ({
    ...sel.params,
    date: date,
    currency: currency,
    firm_name: firmName,
    account_name: accountName,
    account_code: accountCode || sel.params.portfolio,
  });

  const handleDateChange = (value) => {
    setDate(value);
    if (errors.date) {
      setErrors({ ...errors, date: null });
    }
  };

  const handleConfirm = async (submitted) => {
    // `submitted` is the Enter-resolved selection from PortfolioSelector (state
    // hasn't flushed yet); a button click passes a MouseEvent, so fall back to
    // state when it carries no selectedItem.
    const sel = (submitted && submitted.selectedItem) ? submitted : selection;
    const newErrors = {};

    if (!sel.selectedItem) {
      newErrors.portfolio = 'Portfolio is required';
    }

    if (!date) {
      newErrors.date = 'Date is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm(buildParams(sel));
      onClose();
    } catch (error) {
      console.error('Report generation error:', error);
      setErrors({ submit: error.message || 'Failed to generate report' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className={`w-full max-w-2xl rounded-lg shadow-xl ${
          darkMode ? 'bg-[#0A0A0A]' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          darkMode ? 'border-white/10' : 'border-gray-200'
        }`}>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Custom Fixed Income Report with Cash
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg hover:bg-opacity-80 transition-colors ${
              darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            This report displays a portfolio's fixed income holdings as of a selected date.
            Columns include CUSIP, par value, purchase price, total cost, ratings, and call or put date.
          </p>

          {/* Portfolio Field */}
          <div>
            <PortfolioSelector darkMode={darkMode} prefill="" initialSelection={selectionFromParams(initialParams)} onChange={setSelection} onSubmit={handleConfirm}  disabled={isLoading}/>
            {errors.portfolio && (
              <p className="text-red-500 text-xs mt-1">{errors.portfolio}</p>
            )}
          </div>

          {/* Date Field */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Date <span className="text-red-500">*</span>
            </label>
            <MaskedDateField
              value={date}
              max={maxReportDate()}
              onChange={(e) => handleDateChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                darkMode
                  ? 'bg-[#1a1a1a] border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } ${errors.date ? 'border-red-500' : ''}`}
              disabled={isLoading}
            />
            {errors.date && (
              <p className="text-red-500 text-xs mt-1">{errors.date}</p>
            )}
          </div>

          {/* Currency Field */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Reporting Currency
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                darkMode
                  ? 'bg-[#1a1a1a] border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              disabled={isLoading}
            >
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="JPY">JPY - Japanese Yen</option>
              <option value="CAD">CAD - Canadian Dollar</option>
            </select>
          </div>

          {/* Printed report-header inputs */}
          <div className="grid grid-cols-1 gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Firm Name (printed at top of report)
              </label>
              <input
                type="text"
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  darkMode
                    ? 'bg-[#1a1a1a] border-white/10 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                disabled={isLoading}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Account Name
                </label>
                <input
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="e.g. REDACTED-CLIENT"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    darkMode
                      ? 'bg-[#1a1a1a] border-white/10 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Account Code
                </label>
                <input
                  type="text"
                  value={accountCode}
                  onChange={(e) => setAccountCode(e.target.value)}
                  placeholder="e.g. EQY002536"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    darkMode
                      ? 'bg-[#1a1a1a] border-white/10 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className={`p-3 rounded-lg ${
              darkMode ? 'bg-red-900 bg-opacity-50' : 'bg-red-50'
            }`}>
              <p className="text-red-500 text-sm">{errors.submit}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-end gap-3 p-6 border-t ${
          darkMode ? 'border-white/10' : 'border-gray-200'
        }`}>
          <Button
            onClick={onCancel || onClose}
            variant="outline"
            disabled={isLoading}
            className={darkMode ? 'border-gray-600 text-gray-300' : ''}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isLoading ? 'Generating...' : 'OK'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FixedIncomeHoldingsByMaturityPopup;
